// ─────────────────────────────────────────────────────────────────────────────
// NeedsRunPicker.tsx — THE COMPANIES THAT NEED A RUN, AND THE ONE PRESS.
//
// Owner ruling 2026-09-28: "The picker lists the companies that need a Run first —
// qualified, not parked, no v2 script — the eight on top. One press to run exactly
// those — 'Run the unscripted (8)' — queued one after another. Parked companies are
// never included."
//
// Presentational. `DeltaRunControl` reads the list, owns the selection and sends the
// press; this renders what it is handed. A company that cannot be Run is LISTED WITH
// WHY and cannot be picked — absent and unrunnable are different facts.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { MC } from '../missioncontrol/theme';
import { NeedsRunState, RunUnscriptedAnswer, unscripted } from '../../api/deltaNeedsRun';

export interface NeedsRunPickerProps {
  state: NeedsRunState;
  selectedId: string | null;
  onSelect: (companyId: string | null) => void;
  /** Absent → the button is not offered. */
  onRunUnscripted?: () => void;
  /** False while a run or the loop holds the box, or a press is being sent. */
  canBatch: boolean;
  answer?: RunUnscriptedAnswer | null;
  testId?: string;
}

export const RUN_UNSCRIPTED_LABEL = (n: number) => `RUN THE UNSCRIPTED (${n})`;

export default function NeedsRunPicker(
  { state, selectedId, onSelect, onRunUnscripted, canBatch, answer = null, testId = 'delta-needs-run' }: NeedsRunPickerProps,
) {
  const batch = unscripted(state);
  const listed = state.phase === 'read';
  const live = listed && canBatch && batch.length > 0 && !!onRunUnscripted;
  const label = (c: NeedsRunPickerProps['state']['companies'][number]) =>
    (c.business || c.companyId) + (c.fitFlag ? ' · ' + c.fitFlag : '')
    + (c.script === 'v1' ? ' · v1 script' : ' · no script')
    + (c.runnable ? '' : ' · ' + (c.whyNot || 'cannot be run'));

  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 10px 0' }}>
      <label htmlFor={`${testId}-select`} style={{ color: MC.dim, fontSize: 11, letterSpacing: '.06em' }}>
        NEEDS A RUN
      </label>
      <select
        id={`${testId}-select`}
        data-testid={`${testId}-select`}
        aria-label="needs a run"
        value={selectedId && state.companies.some((c) => c.companyId === selectedId) ? selectedId : ''}
        disabled={!listed || state.companies.length === 0}
        onChange={(e) => onSelect(e.target.value || null)}
        style={{
          background: MC.bg2, color: MC.text, border: `1px solid ${MC.line2}`,
          borderRadius: 10, padding: '5px 10px', font: 'inherit', fontSize: 13, minWidth: 260,
        }}
      >
        <option value="">{listed ? `— ${state.companies.length} need a run —` : '— not read —'}</option>
        {state.companies.map((c) => (
          <option key={c.companyId} value={c.companyId} disabled={!c.runnable}>{label(c)}</option>
        ))}
      </select>
      <button
        type="button"
        data-testid={`${testId}-batch`}
        disabled={!live}
        title="RUNS EXACTLY THESE, ONE AFTER ANOTHER — NEVER A PARKED COMPANY"
        onClick={live ? onRunUnscripted : undefined}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: live ? MC.tealSoft : 'transparent',
          border: `1px solid ${live ? MC.tealEdge : MC.line2}`,
          color: live ? MC.teal : MC.dim, borderRadius: 10, padding: '6px 12px',
          font: 'inherit', fontSize: 12, letterSpacing: '.06em', cursor: live ? 'pointer' : 'default',
          boxShadow: live ? `inset 0 1px 0 ${MC.topHighlight}` : 'none',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 10 }}>▶</span>
        {RUN_UNSCRIPTED_LABEL(batch.length)}
      </button>
      {!listed && state.reason && (
        <span style={{ color: MC.muted, fontSize: 12 }}>{state.reason}</span>
      )}
      {answer && (
        <span data-testid={`${testId}-answer`} style={{ color: answer.ok ? MC.muted : MC.amber, fontSize: 12 }}>
          {answer.ok
            ? `${answer.started.length} STARTED, ONE AFTER ANOTHER`
              + (answer.leftOut.length ? ` · LEFT OUT: ${answer.leftOut.map((l) => l.why).join(' · ')}` : '')
            : answer.refused}
        </span>
      )}
    </div>
  );
}
